/**
 * React Query hook for the Inspection Series listing.
 *
 * Contract requirements:
 *  - keepPreviousData: true → stale data stays visible while a new page is loading.
 *  - Errors are not swallowed — they propagate to React Query `error` state.
 *  - The QueryClient is created outside the render body by the feature mount (task-11);
 *    this hook only consumes it via useQuery.
 *  - Query key is built deterministically by seriesQueryKey().
 *  - Search is part of the listing state (IP-15819) and is serialized by seriesStateSerializer.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchInspectionSeries } from '../services/InspectionSeriesService';
import { seriesKeys } from './queryKeys';
/**
 * Fetches inspection series for the given normalized state.
 *
 * Errors thrown by fetchInspectionSeries() are surfaced as `error` in the returned
 * query result — callers must handle the error state explicitly (no swallowing).
 */
export function useSeriesQuery(state, options = {}) {
    return useQuery(seriesKeys.listing(state), () => fetchInspectionSeries(state), {
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        enabled: options.enabled !== false
        // Errors propagate — do NOT add onError swallowing
    });
}
